// STAGING-LAUNCH-READINESS-002-R2 — lightweight "recently viewed" tracker for exit retention.
// CUSTOMER-R4.15-E — persist bounded product shells (no multi-MB catalog bootstrap).
// No PII, no server round-trip.

import type { CatalogV2Product } from "./catalog-v2-types.js";

const RECENT_KEY = "asiastyle.store-v2.recent-products.v1";
const RECENT_SHELLS_KEY = "asiastyle.store-v2.recent-product-shells.v1";
const MAX_RECENT = 8;

export type RecentProductShell = {
  id: string;
  title: string;
  imageUrl: string | null;
  priceUah: number | null;
  isSellable: boolean;
  stockStatus: string;
  brand: string | null;
  categoryKeys: string[];
};

function toShell(product: CatalogV2Product | RecentProductShell): RecentProductShell {
  return {
    id: String(product.id),
    title: String(product.title ?? ""),
    imageUrl: product.imageUrl ?? null,
    priceUah: typeof product.priceUah === "number" ? product.priceUah : null,
    isSellable: product.isSellable !== false,
    stockStatus: product.stockStatus ?? "unknown",
    brand: product.brand ?? null,
    categoryKeys: Array.isArray(product.categoryKeys) ? product.categoryKeys.map(String) : [],
  };
}

function shellToProduct(shell: RecentProductShell): CatalogV2Product {
  const priced = typeof shell.priceUah === "number" && shell.priceUah >= 0;
  return {
    id: shell.id,
    sku: shell.id,
    title: shell.title,
    titleRu: null,
    categoryKeys: shell.categoryKeys,
    categoryName: "",
    imageUrl: shell.imageUrl,
    productUrl: null,
    priceUah: shell.priceUah,
    displayPrice: priced ? String(shell.priceUah) : "",
    isPriced: priced,
    stockStatus: shell.stockStatus,
    isSellable: shell.isSellable,
    sellabilityReason: "",
    brand: shell.brand,
    country: null,
    weight: null,
    regularPriceUah: null,
    salePriceUah: null,
    saleStartsAt: null,
    saleEndsAt: null,
    promotionId: null,
    promotionStatus: null,
    priceUpdatedAt: null,
    sourceRevision: null,
    search: "",
    promProductId: null,
    merchantSku: null,
    merchantArticle: null,
    barcodeGtin: null,
    barcodeVerification: "absent",
    manufacturerPartNumber: null,
    warehouseCode: null,
    sourceSystem: null,
    sourceUpdatedAt: null,
    lastSyncedAt: null,
    firstSeenAt: null,
    catalogArrivedAt: null,
    sourceTitle: null,
    customerTitle: null,
    titleAliases: [],
  };
}

export function recordRecentProduct(productId: string): void {
  const id = String(productId ?? "").trim();
  if (!id) return;
  try {
    const existing = readRecentProductIds().filter((pid) => pid !== id);
    existing.unshift(id);
    window.sessionStorage.setItem(RECENT_KEY, JSON.stringify(existing.slice(0, MAX_RECENT)));
  } catch {
    /* storage unavailable — non-critical UX enhancement */
  }
}

/** Prefer this on PDP open — stores enough for “Ви нещодавно переглядали” cards. */
export function recordRecentProductShell(product: CatalogV2Product | RecentProductShell): void {
  const id = String(product?.id ?? "").trim();
  if (!id) return;
  recordRecentProduct(id);
  try {
    const shell = toShell(product);
    const existing = readRecentProductShellsRaw().filter((p) => p.id !== id);
    existing.unshift(shell);
    window.sessionStorage.setItem(RECENT_SHELLS_KEY, JSON.stringify(existing.slice(0, MAX_RECENT)));
  } catch {
    /* ignore */
  }
}

export function readRecentProductIds(): string[] {
  try {
    const raw = window.sessionStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === "string").slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function readRecentProductShellsRaw(): RecentProductShell[] {
  try {
    const raw = window.sessionStorage.getItem(RECENT_SHELLS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const out: RecentProductShell[] = [];
    for (const row of parsed) {
      if (!row || typeof row !== "object") continue;
      const id = String((row as { id?: string }).id ?? "").trim();
      if (!id) continue;
      const r = row as Partial<RecentProductShell>;
      out.push({
        id,
        title: String(r.title ?? ""),
        imageUrl: r.imageUrl ?? null,
        priceUah: typeof r.priceUah === "number" ? r.priceUah : null,
        isSellable: r.isSellable !== false,
        stockStatus: String(r.stockStatus ?? "unknown"),
        brand: r.brand ?? null,
        categoryKeys: Array.isArray(r.categoryKeys) ? r.categoryKeys.map(String) : [],
      });
    }
    return out.slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

export function readRecentProductShells(): CatalogV2Product[] {
  return readRecentProductShellsRaw().map(shellToProduct);
}

export const RECENT_PRODUCTS_MAX = MAX_RECENT;
